import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  InputBase,
  TextField,
  Menu,
  Chip,
  MenuItem,
  IconButton,
  Button,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  FilterList as FilterIcon,
  Search as SearchIcon,
  CurrencyRupee as RupeeIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

import adminService from "../../../service/adminService";
import dataService from "../../../service/dataService";
import LoadingContext from "../../../context/LoadingContext";
import printTable from "../../../utils/printTable";
import ToastContext from "../../../context/ToastContext";
import AlertContext from "../../../context/AlertContext";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "date",
    numeric: true,
    disablePadding: false,
    label: "Date",
  },
  {
    id: "name",
    numeric: true,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "course",
    numeric: true,
    disablePadding: false,
    label: "Course",
  },
  {
    id: "batch",
    numeric: true,
    disablePadding: false,
    label: "Batch",
  },
  {
    id: "transaction_by",
    numeric: true,
    disablePadding: false,
    label: "Transaction By",
  },
  {
    id: "order_id",
    numeric: true,
    disablePadding: false,
    label: "Order ID",
  },
  {
    id: "status",
    numeric: true,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "amount",
    numeric: true,
    disablePadding: true,
    label: "Amount",
  },
  {
    id: "action",
    numeric: true,
    disablePadding: true,
    label: "Action",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "left" : "center"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { requestSearch, setTableData, rows } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 0 },
        pr: { xs: 0, sm: 0 },
        gap: "1rem",
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Transactions List
      </Typography>
      <Button
        variant="contained"
        endIcon={<PrintIcon />}
        sx={{ background: "#3b5998", paddingLeft: 3, paddingRight: 3 }}
        onClick={() => {
          printTable(rows, headCells, "Transactions List");
        }}
      >
        Print
      </Button>
      <div className="d-flex rounded border">
        <InputBase
          style={{
            width: "10rem",
            height: "2.5rem",
            paddingLeft: "1rem",
          }}
          placeholder="Search Name here..."
          inputProps={{ "aria-label": "search" }}
          onChange={(e) => {
            requestSearch(e.target.value);
          }}
        />
        <IconButton aria-label="search">
          <SearchIcon />
        </IconButton>
      </div>
      {/* <TableFilter setTableData={setTableData} /> */}
    </Toolbar>
  );
};

export default function TransactionTable() {
  const [originalRows, setOriginalRows] = useState([]);
  const [rows, setRows] = useState([]);

  const loading = React.useContext(LoadingContext);
  const alert = React.useContext(AlertContext);
  const toast = React.useContext(ToastContext);

  const requestSearch = (searchedVal) => {
    searchedVal = searchedVal.toUpperCase();
    let filteredRows = originalRows.filter((row) => {
      for (let x in row) {
        if (row[x] !== null && row[x] !== undefined) {
          let row_value = row[x].toUpperCase();
          let result = row_value.includes(searchedVal);
          if (result) return result;
        }
      }
      return false;
    });
    setRows(filteredRows);
  };

  const setTableData = () => {
    loading.incrementLoading();
    adminService.getTransactionsList().then((response) => {
      if (response.data.status === "success") {
        setOriginalRows(response.data.data);
        setRows(response.data.data);
      } else {
        toast.setValue({ open: true, ...response.data });
      }
      loading.decrementLoading();
    });
  };

  useEffect(() => {
    setTableData();
  }, []);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transactionStatusUpdate = (order_id, status) =>{
      alert.setValue({
        open: true,
        status: "warning",
        message:
          status ? "Are you sure you want to decline this transaction?" : "Are you sure you want to approve this transaction?",
        response: () => {
          alert.handleClose();
          if (status === 0) {
          adminService.rejectTransaction(order_id).then((response) => {
            if (response.data.status === "success") {
              toast.setValue({ open: true, ...response.data });
              setTableData();
            } else {
              toast.setValue({ open: true, ...response.data });
            }
          });
        } else {
          adminService.approveTransaction(order_id).then((response) => {
            if (response.data.status === "success") {
              toast.setValue({ open: true, ...response.data });
              setTableData();
            } else {
              toast.setValue({ open: true, ...response.data });
            }
          });
        }
        },
      });
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  let prop = {
    requestSearch: requestSearch,
    setTableData: setTableData,
    rows: rows,
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, padding: 3 }}>
          <EnhancedTableToolbar {...prop} />
          <TableContainer>
            <Table sx={{ minWidth: 750 }} size={"medium"}>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const date = new Date(row.date).toLocaleDateString("en-GB");
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                        <TableCell align="left" id={labelId}>
                          {date}
                        </TableCell>
                        <TableCell align="left" id={labelId}>
                          {row.name}
                        </TableCell>
                        <TableCell align="left">{row.course}</TableCell>
                        <TableCell align="left">{row.batch}</TableCell>
                        <TableCell align="left">{row.transaction_by}</TableCell>
                        <TableCell align="left">{row.order_id}</TableCell>
                        <TableCell align="left">
                          {row.status === 1 || row.status === '1' ? (
                            <Chip
                              label="Success"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip label="Failed" size="small" color="error" />
                          )}
                        </TableCell>
                        <TableCell align="left">
                          <b>
                            {row.amount}
                            <RupeeIcon fontSize="small" />
                          </b>
                        </TableCell>
                        <TableCell align="left">
                            {row.status === 1 || row.status === '1' ? (
                              <Button variant="contained" color="error" onClick={()=>{
                                transactionStatusUpdate(row.order_id, 0);
                              }}>Decline</Button>
                            ):(
                              <Button variant="contained" color="success" onClick={()=>{
                                transactionStatusUpdate(row.order_id, 1);
                              }}>Approve</Button>
                            )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 53 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ marginTop: "20px" }}
          />
        </Paper>
      </Box>
    </>
  );
}

// Filter component
function TableFilter({ setTableData }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const [departments, setDepartments] = React.useState([]);
  const [programmes, setProgrammes] = React.useState([]);
  const [batches, setBatches] = React.useState([]);
  const [membershipTypes, setMembershipTypes] = React.useState([]);

  const [filter, setFilter] = React.useState({
    department_id: 0,
    course_id: 0,
    batch_id: 0,
    membership_type_id: 0,
  });

  React.useEffect(() => {
    dataService.getDepartments().then((res) => {
      if (res.data.status === "success") setDepartments(res.data.data);
    });
    dataService.getCourses().then((res) => {
      if (res.data.status === "success") setProgrammes(res.data.data);
    });
    dataService.getMembershipTypes().then((res) => {
      if (res.data.status === "success")
        setMembershipTypes(res.data.data.filter((value, index) => index !== 0));
    });
  }, []);

  //Function to control open and clos event of filter
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  //Function to handle changes on selection filed
  const handleFetch = (event) => {
    handleClose();
    setTableData(filter);
  };

  const handleChange = (event) => {
    const name = event.target.name,
      value = event.target.value;
    if (name === "department_id") {
      setFilter((prevVal) => ({
        ...prevVal,
        [name]: value,
        course_id: 0,
        batch_id: 0,
      }));
      setBatches([]);
      adminService.getProgrammes(value).then((res) => {
        if (res.data.status === "success") {
          setProgrammes(res.data.data);
        }
      });
    }
    if (name === "course_id") {
      setFilter((prevVal) => ({ ...prevVal, [name]: value, batch_id: 0 }));
      adminService.getBatches(value).then((res) => {
        if (res.data.status === "success") {
          setBatches(res.data.data);
        }
      });
    }
    if (name === "batch_id") {
      setFilter((prevVal) => ({ ...prevVal, [name]: value }));
    }
    if (name === "membership_type_id") {
      setFilter((prevVal) => ({ ...prevVal, [name]: value }));
    }
  };

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <FilterIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem disableRipple>
          <TextField
            id="department_id"
            name="department_id"
            select
            label="Department"
            value={filter.department_id}
            onChange={handleChange}
            variant="standard"
            sx={{ width: "150px" }}
          >
            <MenuItem value="0">All</MenuItem>
            {departments.map((values, index) => (
              <MenuItem key={index} value={values.id}>
                {values.name}
              </MenuItem>
            ))}
          </TextField>
        </MenuItem>
        <MenuItem disableRipple>
          <TextField
            id="course_id"
            name="course_id"
            defaultValue={0}
            select
            label="Course"
            value={filter.course_id}
            onChange={handleChange}
            variant="standard"
            sx={{ width: "150px" }}
          >
            <MenuItem value="0" selected>
              All
            </MenuItem>
            {programmes.map((values, index) => (
              <MenuItem key={index} value={values.id}>
                {values.name}
              </MenuItem>
            ))}
          </TextField>
        </MenuItem>
        <MenuItem disableRipple>
          <TextField
            id="batch_id"
            name="batch_id"
            select
            label="Batch"
            value={filter.batch_id}
            onChange={handleChange}
            variant="standard"
            sx={{ width: "150px" }}
          >
            <MenuItem value="0">All</MenuItem>
            {batches.map((values, index) => (
              <MenuItem key={index} value={values.id}>
                {values.name}
              </MenuItem>
            ))}
          </TextField>
        </MenuItem>
        <MenuItem disableRipple>
          <TextField
            id="membership_type_id"
            name="membership_type_id"
            select
            label="Membership Type"
            defaultValue="0"
            value={filter.membership_type_id}
            onChange={handleChange}
            variant="standard"
            sx={{ width: "150px" }}
          >
            <MenuItem value="0" selected>
              {" "}
              All
            </MenuItem>
            {membershipTypes.map((values, index) => (
              <MenuItem key={index} value={values.id}>
                {values.name}
              </MenuItem>
            ))}
          </TextField>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            variant="contained"
            onClick={handleFetch}
            color="primary"
            fullWidth
          >
            Fetch
          </Button>
        </MenuItem>
      </Menu>
    </div>
  );
}
